import axios, { AxiosInstance, AxiosRequestConfig, CreateAxiosDefaults } from 'axios';

import { AxiosInterceptorConfig } from './types';
import { AxiosInterceptorManager } from './axios-middleware';

/**
 * A wrapper class around Axios that provides enhanced functionality and middleware support
 */
export class AxiosClient {
  private readonly instance: AxiosInstance;

  /**
   * Creates a new AxiosClient instance
   * @param baseURL - The base URL for all requests
   * @param config - Additional Axios configuration options
   */
  private constructor(baseURL: string, config?: Omit<CreateAxiosDefaults, 'baseURL'>) {
    this.instance = axios.create({
      baseURL,
      ...config,
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json;charset=UTF-8',
        ...config?.headers,
      },
    });
  }

  /**
   * Creates a new AxiosClient instance with the specified configuration
   * @param baseURL - The base URL for all requests
   * @param config - Additional Axios configuration options
   * @returns A new AxiosClient instance
   */
  static build(baseURL: string, config?: Omit<CreateAxiosDefaults, 'baseURL'>): AxiosClient {
    return new AxiosClient(baseURL, config);
  }

  /**
   * Creates a new instance with additional configuration merged with the existing one
   * @param config - Additional configuration to merge
   * @returns A new AxiosClient instance with merged configuration
   */
  extend(config: AxiosRequestConfig): AxiosClient {
    const newInstance = axios.create({
      ...this.instance.defaults,
      ...config,
      headers: {
        ...this.instance.defaults.headers,
        ...config.headers,
      },
    });

    return new AxiosClient(newInstance.defaults.baseURL!, {
      ...newInstance.defaults,
      headers: newInstance.defaults.headers,
    });
  }

  /**
   * Returns the underlying Axios instance
   * @returns The configured Axios instance
   */
  get(): AxiosInstance {
    return this.instance;
  }

  /**
   * Applies middleware to the Axios instance
   * @param config - Middleware configuration options
   * @returns The configured Axios instance with middleware applied
   */
  withMiddleware(config: AxiosInterceptorConfig): AxiosInstance {
    return AxiosInterceptorManager.apply(this.instance, config);
  }
}
